import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { usePOSCartStore } from '../stores/posCartStore';

// Use `as any` to bypass stale generated Supabase types (pos_orders columns not in generated types)
const db = supabase as any;

export interface CreateCounterOrderInput {
  order_type: 'counter' | 'takeaway' | 'delivery';
  customer_name?: string;
  customer_phone?: string;
  notes?: string;
  payment_method?: string;
  payment_amount?: number;
  change_amount?: number;
}

/**
 * Create a counter order and immediately complete it with payment
 */
export function useCreateCounterOrder() {
  const queryClient = useQueryClient();
  const { company, user } = useAuthStore();
  const { items, customerName, orderNotes, getSubtotal, getVATAmount, getFiscalStamp, getTotal } = usePOSCartStore();

  return useMutation({
    mutationFn: async (input: CreateCounterOrderInput) => {
      if (!company?.id) throw new Error('No company selected');
      if (!user?.id) throw new Error('No user logged in');
      if (items.length === 0) throw new Error('Cart is empty');

      const subtotal = getSubtotal();
      const vatAmount = getVATAmount();
      const fiscalStamp = getFiscalStamp();
      const total = getTotal();

      // 1. Create order (already completed with payment info)
      const { data: order, error: orderError } = await db
        .from('pos_orders')
        .insert({
          company_id: company.id,
          order_number: `ORD-${Date.now()}`,
          order_type: input.order_type,
          status: 'completed',
          table_id: null,
          customer_name: input.customer_name || customerName || null,
          customer_phone: input.customer_phone || null,
          subtotal: subtotal,
          subtotal_amount: subtotal,
          vat_amount: vatAmount,
          fiscal_stamp: fiscalStamp,
          discount_amount: 0,
          total_amount: total,
          payment_method: input.payment_method || 'cash',
          payment_amount: input.payment_amount || total,
          change_amount: input.change_amount || 0,
          completed_at: new Date().toISOString(),
          notes: input.notes || orderNotes || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // 2. Create order items
      const orderItems = items.map(item => ({
        order_id: order.id,
        product_id: item.productId,
        product_code: item.productCode,
        product_name: item.productName,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        vat_rate: item.vat,
        discount_rate: item.discount,
        discount_amount: 0,
        subtotal: item.quantity * item.unitPrice,
        subtotal_amount: item.quantity * item.unitPrice,
        vat_amount: (item.quantity * item.unitPrice * item.vat) / 100,
        total_amount: item.quantity * item.unitPrice * (1 + item.vat / 100),
        status: 'served',
        notes: item.notes,
      }));

      const { error: itemsError } = await db
        .from('pos_order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // 3. Return order with items for receipt
      return {
        ...order,
        items: orderItems.map((item, idx) => ({
          ...item,
          id: `item-${idx}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
      queryClient.invalidateQueries({ queryKey: ['active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-orders'] });
    },
  });
}

/**
 * Complete counter order (mark as completed and process payment)
 */
export function useCompleteCounterOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      paymentMethod = 'cash',
      paymentAmount,
      changeAmount,
    }: {
      orderId: string;
      paymentMethod?: string;
      paymentAmount?: number;
      changeAmount?: number;
    }) => {
      const { data, error } = await db
        .from('pos_orders')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          payment_method: paymentMethod,
          payment_amount: paymentAmount,
          change_amount: changeAmount,
        })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
      queryClient.invalidateQueries({ queryKey: ['active-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pos-orders'] });
    },
  });
}
